import * as React from 'react';
import styles from './index.less';
import { Input } from 'antd';
import { ChangeEventHandler, KeyboardEventHandler } from 'react';
import type { TextAreaRef } from 'antd/es/input';
import * as AHooks from 'ahooks';
import { FUtil } from '@freelog/tools-lib';

interface FInput_PinyinSafeTextCounter_MultiLine_Props {
  value: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  size?: 'small' | 'middle';
  style?: React.CSSProperties;
  lengthLimit?: number;
  hasError?: boolean;

  onPressEnter?: KeyboardEventHandler<HTMLTextAreaElement>;
  onKeyUp?: KeyboardEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;

  onChangeValue?(value: string): void;
}

function FInput_PinyinSafeTextCounter_MultiLine(
  {
    value,
    placeholder = '',
    autoFocus = false,
    disabled = false,
    className = '',
    size = 'middle',
    style = {},
    lengthLimit = 600,
    hasError = false,
    onPressEnter,
    onKeyUp,
    onBlur,
    onChangeValue,
  }: FInput_PinyinSafeTextCounter_MultiLine_Props,
  ref: React.Ref<TextAreaRef> | undefined,
) {
  const [$flag, set$flag, get$flag] = FUtil.Hook.useGetState<boolean>(true);
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>('');

  AHooks.useDebounceEffect(
    () => {
      if (value !== get$value()) {
        set$value(value);
      }
    },
    [value],
    {
      wait: 10,
    },
  );

  /** 处理文本长度限制，如果超出字数限制，截断新输入超出的文本 */
  function handleTextLimitChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    event.preventDefault();
    const v = event.target.value;
    const cursorPos = event.target.selectionStart ?? 0;
    if (lengthLimit <= 0 || v.length <= lengthLimit) {
      onChangeValue?.(v);
      set$value(v);
      return;
    }

    const beforeCursor = v.substring(0, cursorPos);
    const afterCursor = v.substring(cursorPos);

    const exceed = v.length - lengthLimit;
    const truncatedBeforeCursor = beforeCursor.substring(0, beforeCursor.length - exceed);
    const truncatedValue = truncatedBeforeCursor + afterCursor;
    onChangeValue?.(truncatedValue);
    set$value(truncatedValue);
    if (event.target.selectionStart !== truncatedBeforeCursor.length) {
      setTimeout(() => {
        (event.target as HTMLTextAreaElement).setSelectionRange(
          truncatedBeforeCursor.length,
          truncatedBeforeCursor.length,
        );
      }, 1);
    }
  }

  return (
    <div className={styles.introduction}>
      <Input.TextArea
        ref={ref}
        autoFocus={autoFocus}
        disabled={disabled}
        value={$value}
        placeholder={placeholder}
        className={[
          className,
          styles.TextArea,
          disabled
            ? styles.disabled_FInput_PinyinSafeTextCounter
            : styles.FInput_PinyinSafeTextCounter,
          hasError ? styles.InputError : '',
        ].join(' ')}
        style={{
          height: size === 'middle' ? 38 : 32,
          ...style,
        }}
        onPressEnter={onPressEnter}
        onKeyUp={onKeyUp}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
          if (get$flag()) {
            handleTextLimitChange(event);
          } else {
            set$value(event.target.value);
          }
        }}
        onBlur={onBlur}
        onCompositionStart={(event) => {
          set$flag(false);
        }}
        onCompositionEnd={(event: React.CompositionEvent<HTMLTextAreaElement>) => {
          set$flag(true);
          handleTextLimitChange(event as any);
        }}
        autoSize={{ minRows: 4, maxRows: 20 }}
      />
      {lengthLimit > 0 && (
        <span
          className={[styles.FInputWordCount, value.length > lengthLimit ? styles.beyond : ''].join(
            ' ',
          )}
        >
          {lengthLimit - value.length}
        </span>
      )}
    </div>
  );
}

export default React.forwardRef(FInput_PinyinSafeTextCounter_MultiLine);
